/**
 * useWatchSync
 *
 * Phone-side bridge between the Redux store and the Apple Watch companion app.
 *
 * Responsibilities:
 *  - Listen for inbound watch events (request_plan_data, request_emergency_data,
 *    file_received, activation) via WCSession → WatchBridgeNativeModule → JS.
 *  - Respond with the relevant plan / note / emergency-contact / QR data from Redux.
 *  - Proactively push a plans_update whenever the project list changes.
 *  - Upload voice-note files received from the watch to the backend.
 *
 * Must be called only when `enabled` is true (watch companion feature gate + authorized).
 */

import { useEffect, useMemo, useRef, useCallback } from 'react';
import { AppState } from 'react-native';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import type { RootState } from '../store';
import { onWatchSyncStatus, sendWatchMessage, pushWatchContext } from '../watch';
import type { WatchSyncStatusEvent } from '../watch/bridge/WatchConnectivityBridge';
import type { WatchPlanSummary, WatchEmergencyContact, WatchVaultItem, WatchWellnessSummary } from '../watch/contracts';
import { apiPost, apiProjectsGet, apiProjectsPost, apiWellnessGet, apiWellnessPost, apiRecordsPost } from '../utilities/apiClient';
import { localDayKey } from '../utilities/healthDayKey';
import { openProjectVault } from '../utilities/vaultUnlock';
import {
  decryptCredential,
  encryptCredential,
  generateShareKey,
  bytesToHex,
} from '../utilities/vaultCrypto';
import type { EncryptedCredential } from '../utilities/vaultCrypto';
import config from '../config';
import { buildAvatarUrl } from '../utilities/avatarUrl';
import { doseText } from '../utilities/medicationStrength';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const QRCodeLib = require('qrcode');

function buildQRMatrix(url: string): { matrix: number[]; size: number } {
  try {
    const qrData = QRCodeLib.create(url, { errorCorrectionLevel: 'M' });
    const matrix = Array.from(qrData.modules.data as Uint8Array) as number[];
    const size = Math.round(Math.sqrt(matrix.length));
    return { matrix, size };
  } catch {
    return { matrix: [], size: 0 };
  }
}
import { createNote, fetchNotes } from '../store/actions/notes';
import { fetchMedications } from '../store/actions/medications';
import { fetchContacts } from '../store/actions/contacts';
import { fetchVaultEntries } from '../store/actions/vault';
import { watchInfo, watchWarn, watchError } from '../watch/telemetry';
// Reused rather than reimplemented: the watch shows the same rollup the phone
// does, so it must format it the same way. Both live on VitalsCard already.
import { WELLNESS_STATUS_STYLE, vitalSummary } from '../utilities/wellnessFormat';
import { buildWatchCheckInCatalog } from '../utilities/manualCheckIn';
import { recordWellnessCheckIn } from '../store/actions/wellness';

/** A watch SOS token is re-minted once it has less than this left (tokens last 48 hours). */
const SOS_TOKEN_REUSE_MS = 24 * 60 * 60 * 1000;
/** A relayed SOS older than this is dropped: the watch has long since told the wearer to call 911. */
const SOS_TRIGGER_MAX_AGE_MS = 2 * 60 * 1000;

/**
 * Module-level pending vault approval triggered by a local notification
 * (when the iOS app was backgrounded and the watch sent transferUserInfo).
 * Set from RootNavigator's notification response handler; consumed by the
 * hook effect below once `enabled` is true and Redux data is ready.
 */
interface PendingWatchVaultApprovalData {
  credentialId: number;
  projectId: string;
  label: string;
  credentialType: string;
}
let _pendingWatchVaultApproval: PendingWatchVaultApprovalData | null = null;

export function setPendingWatchVaultApproval(data: PendingWatchVaultApprovalData): void {
  _pendingWatchVaultApproval = data;
}

/**
 * Passed to onVaultApproval when the watch requests a credential QR and the
 * VEK is not already in the session cache. The phone UI should show an
 * approval prompt, then call approve() or deny().
 *
 * approve() derives the VEK, caches it in memory, and resolves the pending
 * QR generation. Returns true on success, false if derivation fails.
 * deny() sends vault_qr_error: auth_required to the watch.
 */
export interface WatchVaultApprovalRequest {
  credentialId: number;
  projectId: string;
  label: string;
  credentialType: string;
  approve: () => Promise<boolean>;
  deny: () => void;
}

/**
 * Passed to `onNoteProcessed` after a watch-sourced voice note finishes
 * uploading. The phone shows a celebratory bottom sheet with the saved
 * title + AI summary so the user can admire their work — the watch doesn't
 * have room for this.
 *
 * `summary` is the `response` column on the note row (populated by the
 * backend summarization model). May be missing if summarization was
 * skipped (e.g. feature flag off, empty body, model error) — the sheet
 * falls back to showing `body` in that case.
 */
export interface WatchProcessedNoteRequest {
  noteId: string | null;
  projectId: string;
  projectName: string | null;
  title: string;
  summary: string | null;
  body: string | null;
  type: string | null;
  createdAt: string | null;
  receivedAt: number;
}

export function useWatchSync(
  enabled: boolean,
  onVaultApproval?: (req: WatchVaultApprovalRequest) => void,
  onNoteProcessed?: (req: WatchProcessedNoteRequest) => void,
): void {
  const dispatch = useAppDispatch();

  const projectsList = useSelector((state: RootState) => state.projects.list);
  const contactsList = useSelector((state: RootState) => state.contacts.list);
  const notesList = useSelector((state: RootState) => state.notes.list);
  const medicationsList = useSelector((state: RootState) => state.medications.list);
  const accountId = useSelector(
    (state: RootState) => state.session.account?.account_id as string | undefined,
  );
  // Account-level SOS capability flag. Mirrors EmergencySOSScreen's logic:
  // the project list (`get-all`) doesn't include `recipient_sos_capable`, so
  // relying on it alone leaves the watch showing "SOS unavailable" even when
  // the account feature is enabled. The single-project fetch sets that field,
  // but until then we should fall back to the account-level capability.
  const accountSosCapable = useSelector(
    (state: RootState) => (state.session.account as any)?.features?.emergency_sos as boolean | undefined,
  );
  // Per-screen visibility toggles set in WatchSettingsScreen. Treat undefined
  // (column missing on a pre-migration row) as TRUE so old accounts see every
  // tab. Watch reads these to gate which pages it includes in its TabView.
  const accountFeatures = useSelector(
    (state: RootState) => (state.session.account as any)?.features as Record<string, boolean | undefined> | undefined,
  );
  const screenSettings = useMemo(() => ({
    emergency:   accountFeatures?.watch_screen_emergency   !== false,
    medications: accountFeatures?.watch_screen_medications !== false,
    vault:       accountFeatures?.watch_screen_vault       !== false,
    contacts:    accountFeatures?.watch_screen_contacts    !== false,
    wellness:    accountFeatures?.watch_screen_wellness    !== false,
  }), [accountFeatures]);
  const cognitoId = useSelector(
    (state: RootState) => state.authentication.user?.cognito_id as string | undefined,
  );
  const userPin = useSelector(
    (state: RootState) => (state.authentication.user as any)?.pin as string | undefined,
  );
  const vaultList = useSelector(
    (state: RootState) => (state as any).vault?.list as Record<string, EncryptedCredential[]> | undefined,
  );
  const currentUser = useSelector(
    (state: RootState) => state.authentication.user as any,
  );

  // Stable refs for data that the event handler reads — avoids tearing down and
  // re-registering the subscription on every Redux state update.
  const projectsRef = useRef(projectsList);
  const contactsRef = useRef(contactsList);
  const notesRef = useRef(notesList);
  const medicationsRef = useRef(medicationsList);
  const accountIdRef = useRef(accountId);
  const accountSosCapableRef = useRef(accountSosCapable);
  const screenSettingsRef = useRef(screenSettings);
  const cognitoIdRef = useRef(cognitoId);
  const userPinRef = useRef(userPin);
  const vaultListRef = useRef(vaultList);
  const currentUserRef = useRef(currentUser);
  // In-memory VEK cache — avoids re-deriving the VEK on every watch QR request.
  // Keyed by projectId; entries expire after 5 minutes.
  const watchVekCache = useRef<Map<string, { vek: Uint8Array; exp: number }>>(new Map());
  // Dedup SOS replies — the watch ships them via BOTH transferFile (durable)
  // and sendMessage (fast), so without this the reply would be saved twice.
  // Bounded LRU semantics: cap at 200 ids to avoid unbounded growth.
  const seenSosReplyIds = useRef<Set<string>>(new Set());
  // Stable ref for the approval callback so async handlers always call the latest version.
  const onVaultApprovalRef = useRef(onVaultApproval);
  useEffect(() => { onVaultApprovalRef.current = onVaultApproval; }, [onVaultApproval]);
  const onNoteProcessedRef = useRef(onNoteProcessed);
  useEffect(() => { onNoteProcessedRef.current = onNoteProcessed; }, [onNoteProcessed]);

  useEffect(() => { projectsRef.current = projectsList; }, [projectsList]);
  useEffect(() => { contactsRef.current = contactsList; }, [contactsList]);
  useEffect(() => { notesRef.current = notesList; }, [notesList]);
  useEffect(() => { medicationsRef.current = medicationsList; }, [medicationsList]);
  useEffect(() => { accountIdRef.current = accountId; }, [accountId]);
  useEffect(() => { accountSosCapableRef.current = accountSosCapable; }, [accountSosCapable]);
  useEffect(() => { screenSettingsRef.current = screenSettings; }, [screenSettings]);
  useEffect(() => { cognitoIdRef.current = cognitoId; }, [cognitoId]);
  useEffect(() => { userPinRef.current = userPin; }, [userPin]);
  useEffect(() => { vaultListRef.current = vaultList; }, [vaultList]);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);

  // ----- Data builders -----

  const buildPlans = useCallback((): WatchPlanSummary[] =>
    (Object.values(projectsRef.current) as any[]).flatMap((p: any) => {
      const myId = cognitoIdRef.current;
      const isRecipient = myId && p.recipient_cognito_id === myId;

      // Non-recipients need the `watch` project permission to see the plan on the watch.
      if (!isRecipient) {
        const member = Array.isArray(p.users)
          ? p.users.find((u: any) => u.cognito_id === myId)
          : null;
        const perms: string[] = Array.isArray(member?.project_permissions)
          ? member.project_permissions
          : [];
        if (!perms.includes('watch')) return [];
      }

      const recipientFirst = p.first_name ?? p.blob?.first_name ?? '';
      const recipientLast = p.last_name ?? p.blob?.last_name ?? '';
      const recipientName = [recipientFirst, recipientLast].filter(Boolean).join(' ') || null;
      // Caregivers with the `watch` permission can trigger SOS on the recipient's behalf.
      const hasCaregiverSOSAccess = !isRecipient && (() => {
        const member = Array.isArray(p.users)
          ? p.users.find((u: any) => u.cognito_id === myId)
          : null;
        const perms: string[] = Array.isArray(member?.project_permissions)
          ? member.project_permissions
          : [];
        return perms.includes('watch');
      })();

      // Account-level capability is the source of truth — the project list
      // endpoint omits `recipient_sos_capable`, so falling back to that alone
      // would leave the watch showing "SOS unavailable" until the user opens
      // the per-project fetch. Mirror EmergencySOSScreen's resolution order.
      const accountFlag = accountSosCapableRef.current;
      const sosCapable = accountFlag !== undefined
        ? !!accountFlag
        : !!p.recipient_sos_capable;

      const plan: WatchPlanSummary = {
        projectId: String(p.id),
        name: p.project_name || recipientName || 'Unnamed Plan',
        // The avatar endpoint returns 204 when the user has no avatar;
        // AsyncImage on the watch falls back to the initials circle gracefully.
        avatarUrl: p.recipient_cognito_id
          ? buildAvatarUrl(
            p.recipient_cognito_id,
            p.updated_at ? String(p.updated_at) : null,
          )
          : null,
        recipientName,
        lastUpdatedAt: p.updated_at ?? null,
        isOwnPlan: !!isRecipient,
        sosEnabled: !!p.emergency_sos_enabled,
        sosCapable,
        caregiverSOSAvailable: hasCaregiverSOSAccess && !!p.emergency_sos_enabled && sosCapable,
        // Tokens are fetched async after buildPlans; start as null and filled in by enrichPlansWithSOSTokens.
        sosToken: null,
        sosTokenExpiresAt: null,
        sosDirectUrl: null,
      };
      return [plan];
    }), []);

  /**
   * The SOS token on the watch for each plan. The server keeps one token per
   * plan and every mint replaces it, so minting on each plans push (activation,
   * reachability, foreground, every plan-list change) left the watch holding a
   * token the server had already killed whenever a push didn't land. One is
   * minted only when there's none or it has under a day left; the watch reports
   * a used token with `sos_token_spent`.
   */
  const sosTokensRef = useRef(new Map<string, { token: string; expiresAt: string | null }>());
  // Pushes run side by side (launch fires the plan-list push and the watch's
  // request together): they share one mint per plan, or the second mint would
  // kill the token the first push already delivered.
  const sosMintsRef = useRef(new Map<string, Promise<{ token: string; expiresAt: string | null } | undefined>>());

  /**
   * Fill in the watch-direct SOS token for each plan where SOS is available
   * and the caller is the recipient. Mutates the plans array in-place and
   * returns it for convenience.
   */
  const enrichPlansWithSOSTokens = useCallback(async (plans: WatchPlanSummary[]): Promise<WatchPlanSummary[]> => {
    const accountId = accountIdRef.current;
    const cognitoId = cognitoIdRef.current;
    if (!accountId || !cognitoId) return plans;

    await Promise.allSettled(
      plans.map(async (plan) => {
        if (!plan.isOwnPlan || !plan.sosEnabled || !plan.sosCapable) {
          sosTokensRef.current.delete(plan.projectId);
          return;
        }
        let held = sosTokensRef.current.get(plan.projectId);
        const msLeft = held?.expiresAt ? Date.parse(held.expiresAt) - Date.now() : 0;
        if (!held || !(msLeft > SOS_TOKEN_REUSE_MS)) {
          let mint = sosMintsRef.current.get(plan.projectId);
          if (!mint) {
            mint = apiProjectsPost(`/generate-sos-token/${plan.projectId}/${accountId}/${cognitoId}`, {})
              .then((res: any) => (res?.success && res.payload?.token
                ? { token: res.payload.token as string, expiresAt: (res.payload.expiresAt ?? null) as string | null }
                : undefined))
              // Non-fatal — the watch keeps relaying through the phone
              .catch(() => undefined)
              .finally(() => sosMintsRef.current.delete(plan.projectId));
            sosMintsRef.current.set(plan.projectId, mint);
          }
          held = await mint;
          if (held) sosTokensRef.current.set(plan.projectId, held);
          else sosTokensRef.current.delete(plan.projectId);
        }
        if (held) {
          plan.sosToken = held.token;
          plan.sosTokenExpiresAt = held.expiresAt;
          plan.sosDirectUrl = `${config.projectsApi.endpoint}/emergency-sos-direct/${plan.projectId}`;
        }
      }),
    );
    return plans;
  }, []);

  /**
   * Fire the `onNoteProcessed` callback (if one is registered) with the
   * server-side note fields the phone UI needs to render its "new note
   * processed" bottom sheet.
   *
   * Pulls the project name out of the Redux `projectsRef` snapshot by id.
   * Falls back to the `fallbackTitle` the watch-sync handler already built
   * (usually `Watch Note — <timestamp>`) when the backend hasn't populated
   * a better `title` column yet.
   */
  const notifyNoteProcessed = useCallback(
    (projectId: string, payload: any, fallbackTitle: string, fallbackBody: string) => {
      if (!onNoteProcessedRef.current) return;
      const project = (projectsRef.current as any)?.[projectId]
        ?? Object.values(projectsRef.current as any).find((p: any) => String(p?.id) === String(projectId));
      // Mirror buildPlans's name resolution: project_name is the authoritative
      // label, but for recipient-only projects it may be blank — fall through
      // to the recipient's first+last. Use `||` so empty strings don't stick.
      const projectLabel = (project as any)?.project_name;
      const first = (project as any)?.first_name ?? (project as any)?.blob?.first_name ?? '';
      const last = (project as any)?.last_name ?? (project as any)?.blob?.last_name ?? '';
      const recipientName = [first, last].filter(Boolean).join(' ');
      const projectName = projectLabel || recipientName || null;
      const noteId = payload?.id ?? payload?.note_id ?? null;
      const title = (payload?.title as string | undefined) || fallbackTitle;
      // `response` is the AI-summarized content (see
      // backend/api/client/controllers/notes/create/index.js). May be null
      // if summarization was skipped — the sheet falls back to `body`.
      const summary = (payload?.response as string | undefined) || null;
      const body = (payload?.body as string | undefined) || fallbackBody || null;
      onNoteProcessedRef.current({
        noteId: noteId != null ? String(noteId) : null,
        projectId,
        projectName,
        title,
        summary,
        body,
        type: (payload?.type as string | undefined) ?? 'voice',
        createdAt: (payload?.created_at as string | undefined) ?? null,
        receivedAt: Date.now(),
      });
    },
    [],
  );

  const buildNotes = useCallback((projectId: string) => {
    const list: any[] = notesRef.current[projectId] ?? [];
    return list
      .filter((n: any) => (n.type ?? 'text') === 'voice')
      .slice(0, 30)
      .map((n: any) => ({
        id: String(n.id ?? n.note_id),
        projectId,
        text: n.body ?? n.note_text ?? null,
        // response = AI summary shown as transcript snippet on Watch
        response: n.response ?? null,
        createdAt: n.created_at ?? new Date().toISOString(),
        type: 'voice',
        // raw is a boolean flag in list responses — true means audio is stored
        hasRemoteAudio: n.raw === true || typeof n.raw === 'string',
        synced: true,
      }));
  }, []);

  const buildMedications = useCallback((projectId: string) => {
    const list: any[] = medicationsRef.current[projectId] ?? [];
    return list.map((m: any) => ({
      id: String(m.id ?? m.medication_id),
      projectId,
      name: m.name ?? m.med_name ?? 'Unknown',
      // A dose of 0 means none was entered — the medications table defaults
      // an omitted dose to 0, not null.
      dose: doseText(m.dose) || null,
      unit: m.unit ?? null,
      frequency: m.frequency ?? null,
      instructions: m.notes ?? null,
    }));
  }, []);

  /**
   * Project the wellness snapshot into the flat shape the watch renders.
   *
   * Unlike notes/meds/contacts, this is NOT read from Redux: no slice holds
   * it (VitalsCard and WellnessScreen each keep it in local state), and it's
   * live-computed server-side anyway — a cached copy would be the one thing
   * you must not show stale on a screen whose job is "how are they right now".
   * So it's a fetch per request, which is what the watch's explicit
   * `request_wellness` is for.
   *
   * Anything that goes wrong resolves to `available: false` rather than
   * throwing: the watch's job here is to show a truthful placeholder, and a
   * rejected promise would leave it spinning until its 6-second timeout.
   */
  const buildWellness = useCallback(async (projectId: string): Promise<WatchWellnessSummary> => {
    // NOTE: default state is 'unknown', NOT 'off'. They are different facts and
    // the watch says different things about them: 'off' means the feature was
    // never set up ("set it up on your iPhone"), while 'unknown' means we could
    // not find out. Reporting a dropped request as 'off' told people their
    // wellness monitoring was disabled when it was running fine.
    const unavailable = (state = 'unknown', isRecipient = false): WatchWellnessSummary => ({
      projectId, state, isRecipient,
      available: false, canCheckIn: false, canRequest: false, canRecord: false,
      status: 'quiet_expected', statusLabel: '', statusColor: '#6B7280',
      headline: '', lastActivityAt: null, vitals: [], asOf: new Date().toISOString(),
    });

    const aId = accountIdRef.current;
    const cId = cognitoIdRef.current;
    if (!aId || !cId) return unavailable();

    try {
      const res: any = await apiWellnessGet(`/snapshot/${projectId}/${aId}/${cId}`);
      if (!res?.success) return unavailable();

      const gate = res.payload?.gate;
      const rollup = res.payload?.rollup;
      const state = gate?.feature?.state || 'off';
      const isRecipient = !!gate?.viewer?.is_recipient;
      const canView = !!gate?.viewer?.can_view;

      // A gate-only response (feature dark, or viewer can't see it) carries no
      // rollup. Report the state so the watch can say WHY rather than "no data".
      if (!gate?.feature?.enabled || !canView || !rollup) return unavailable(state, isRecipient);

      const style = WELLNESS_STATUS_STYLE[rollup.status] || WELLNESS_STATUS_STYLE.quiet_expected;
      return {
        projectId,
        state,
        isRecipient,
        available: true,
        // Recipient-only on the server (im-ok 403s everyone else), so the
        // button is offered on exactly the same condition.
        canCheckIn: isRecipient && state === 'active',
        canRequest: !isRecipient && canView && state === 'active',
        // Wider than canCheckIn on purpose: manual-entry authorizes the
        // recipient OR any viewer with the wellness permission, which is the
        // caregiver standing there with a blood-pressure cuff. Not
        // state-gated the way the other two are — the endpoint stores
        // readings regardless of wellness state, since the health store is
        // its own domain and predates wellness — but it is only reachable
        // from the active branch of the wellness tab, so placement supplies
        // the state condition the flag deliberately does not.
        canRecord: isRecipient || canView,
        status: rollup.status,
        statusLabel: style.label,
        statusColor: style.color,
        headline: rollup.headline || '',
        lastActivityAt: rollup.last_activity_at || null,
        // Three rows is what fits a 41mm screen without scrolling past the
        // check-in button — the same cut VitalsCard makes for its own reasons.
        vitals: (rollup.vitals || []).slice(0, 3).map((v: any) => ({
          key: v.key,
          label: v.label,
          value: vitalSummary(v),
          state: v.state,
        })),
        // Data freshness, not compute time. A watch face is the most
        // glanceable surface we have and the least able to caveat itself, so
        // shipping it a timestamp that always says "now" is the worst place
        // for this bug to live. Falls back to as_of only so an older payload
        // still renders something rather than blanking the complication.
        asOf: rollup.data_as_of || rollup.as_of || new Date().toISOString(),
      };
    } catch (err: any) {
      watchWarn('Failed to build wellness summary for watch', { projectId, error: err?.message });
      return unavailable();
    }
  }, []);

  const buildEmergencyContacts = useCallback((projectId: string, overrideList?: any[]): WatchEmergencyContact[] => {
    const list: any[] = overrideList ?? contactsRef.current[projectId] ?? [];
    return list
      .filter((c: any) => {
        // Only include contacts explicitly marked as emergency contacts with a phone number.
        if (!c.emergency) return false;
        const phone = c.phone ?? c.phone_number;
        return phone != null && phone !== '';
      })
      .map((c: any) => ({
        contactId: String(c.id ?? c.contact_id),
        name: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.name || 'Unknown',
        role: c.role ?? c.relationship ?? c.contact_type ?? null,
        phone: c.phone ?? c.phone_number ?? null,
        canMessage: (c.phone ?? c.phone_number) != null,
      }))
      .filter((c): c is WatchEmergencyContact => c.phone != null);
  }, []);

  const buildVaultItems = useCallback((projectId: string): WatchVaultItem[] => {
    const list = vaultListRef.current?.[projectId] ?? [];
    const warmCache = watchVekCache.current.get(projectId);
    const warmVek = (warmCache && warmCache.exp > Date.now()) ? warmCache.vek : null;

    return list
      .filter((c) => c.watch_shared === true)
      .map((c) => {
        let hasTOTP: boolean | undefined;
        // When the VEK is already warm we can cheaply check for a TOTP secret
        // without re-prompting the user — just peek at the decrypted payload.
        if (warmVek && c.credential_type === 'login') {
          try {
            const plain = decryptCredential(warmVek, c.encrypted_data, c.iv, c.auth_tag);
            hasTOTP = !!(plain?.otp_secret);
          } catch {
            hasTOTP = undefined;
          }
        }
        return {
          credentialId: c.id,
          projectId,
          label: c.label,
          credentialType: c.credential_type,
          usernameHint: c.username_hint,
          hasTOTP,
        };
      });
  }, []);

  // ----- SOS reply handler -----

  /// The recipient on the watch recorded a voice reply to a `caregiver_message`.
  /// The watch ships it via BOTH `transferFile` (durable) and `sendMessage`
  /// (fast path) so we dedupe by messageId. Reply audio is persisted as a
  /// project note titled "Reply to <senderName>" so it surfaces in the
  /// conversation history just like any other voice note.
  const handleSosReply = useCallback(
    async (args: {
      source: 'file' | 'inline';
      projectId: string | undefined;
      localPath?: string | undefined;
      audioBase64?: string | undefined;
      messageId: string | undefined;
      senderName: string | undefined;
      senderCognitoId: string | undefined;
    }) => {
      const { source, projectId, localPath, audioBase64, messageId, senderName, senderCognitoId } = args;
      if (!projectId || !messageId) {
        watchWarn('sos_reply missing projectId or messageId', { projectId, messageId });
        return;
      }

      // Dedupe — first arrival wins, second is a no-op.
      if (seenSosReplyIds.current.has(messageId)) {
        watchInfo('watch-sync', 'sos_reply duplicate suppressed', { messageId, source });
        return;
      }
      seenSosReplyIds.current.add(messageId);
      // Cap the dedupe set so it doesn't grow unbounded over a long session.
      if (seenSosReplyIds.current.size > 200) {
        const firstKey = seenSosReplyIds.current.values().next().value;
        if (firstKey) seenSosReplyIds.current.delete(firstKey);
      }

      try {
        let rawDataUrl: string;
        if (source === 'inline') {
          if (!audioBase64) {
            watchWarn('sos_reply_inline missing audioBase64', { messageId });
            seenSosReplyIds.current.delete(messageId);
            return;
          }
          rawDataUrl = `data:audio/m4a;base64,${audioBase64}`;
        } else {
          if (!localPath) {
            watchWarn('sos_reply file missing localPath', { messageId });
            seenSosReplyIds.current.delete(messageId);
            return;
          }
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          const fs = require('expo-file-system/legacy') as {
            readAsStringAsync: (uri: string, opts: Record<string, string>) => Promise<string>;
            EncodingType?: { Base64?: string };
          };
          const base64 = await fs.readAsStringAsync(`file://${localPath}`, {
            encoding: fs.EncodingType?.Base64 ?? 'base64',
          });
          rawDataUrl = `data:audio/m4a;base64,${base64}`;
        }

        const replyTo = senderName?.trim() || 'caregiver';
        const title = `Reply to ${replyTo}`;
        const body = `Voice reply to ${replyTo}'s caregiver message`;

        watchInfo('watch-sync', 'Uploading SOS reply note', { projectId, messageId, source });
        const result = await dispatch(
          createNote(projectId, {
            type: 'voice',
            raw: rawDataUrl,
            body,
            title,
            query: title,
          }) as any,
        );
        if (result?.success === false) {
          watchError('SOS reply upload rejected', { projectId, messageId, message: result?.message });
          // Allow a retry on the next inbound delivery of the same messageId.
          seenSosReplyIds.current.delete(messageId);
          return;
        }
        dispatch(fetchNotes(projectId) as any).catch(() => {});
        watchInfo('watch-sync', 'SOS reply saved', { projectId, messageId, source });

        // Notify the original caregiver that we've replied. Mirrors the
        // outbound /projects/caregiver-message call EmergencySOSScreen makes,
        // but with direction=reply_to_sender so the backend pushes only the
        // original caregiver instead of project.recipient_cognito_id.
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        const noteId = result?.payload?.id ?? result?.payload?.note_id;
        if (senderCognitoId && aId && cId && noteId) {
          try {
            const cfg = (await import('../config')).default;
            const audioUrl = `${cfg.apiGateway.endpoint}/notes/get-note-audio/${noteId}/${aId}/${cId}`;
            await apiProjectsPost(`/caregiver-message/${projectId}/${aId}/${cId}`,
              {
                direction: 'reply_to_sender',
                messageId,
                audioUrl,
                senderName: senderName?.trim() || undefined,
                noteId: String(noteId),
                targetCognitoId: senderCognitoId,
              },
            );
          } catch (err: any) {
            watchWarn('Failed to notify caregiver of SOS reply', { projectId, messageId, error: err?.message });
          }
        }
      } catch (err: any) {
        watchError('SOS reply upload failed', { projectId, messageId, error: err?.message });
        seenSosReplyIds.current.delete(messageId);
      }
    },
    [dispatch],
  );

  // ----- Event handler -----

  const handleEvent = useCallback(async (event: WatchSyncStatusEvent) => {
    // Inbound events from the watch arrive with `type` and `payload` fields.
    const eventType = event.type ?? (event.payload?.type as string | undefined);
    const payload = event.payload ?? {};

    switch (eventType) {
      case 'activation': {
        // Watch WCSession just activated — push full plan list with fresh SOS tokens.
        // updateApplicationContext for real-device background delivery.
        // sendMessage as well because updateApplicationContext is silently dropped
        // by the simulator (Apple bug since Xcode 13); sendMessage works on both.
        const activationPlans = await enrichPlansWithSOSTokens(buildPlans());
        const activationDisplayName = getCaregiverDisplayName();
        watchInfo('watch-sync', 'Pushing plans on activation', { count: activationPlans.length });
        await pushWatchContext({ type: 'plans_update', plans: activationPlans, caregiverDisplayName: activationDisplayName, screen_settings: screenSettingsRef.current }).catch(() => {});
        await sendWatchMessage({ type: 'plans_update', plans: activationPlans, caregiverDisplayName: activationDisplayName, screen_settings: screenSettingsRef.current }).catch(() => {});
        // Push PIN config so the Watch lock screen is always in sync.
        const pinMsg = {
          type: 'pin_update',
          pin_hash: userPinRef.current ?? null,
          cognito_id: cognitoIdRef.current ?? null,
        };
        await pushWatchContext(pinMsg).catch(() => {});
        await sendWatchMessage(pinMsg).catch(() => {});
        break;
      }

      case 'reachability_change': {
        // The watch just came into Bluetooth/WiFi range (or went out).
        // When it becomes reachable, push a fresh plans snapshot immediately so the
        // watch wakes up with current data without waiting for its next request cycle.
        if (payload.isReachable) {
          const reachPlans = await enrichPlansWithSOSTokens(buildPlans());
          const reachDisplayName = getCaregiverDisplayName();
          watchInfo('watch-sync', 'Watch became reachable — pushing fresh plans', { count: reachPlans.length });
          await sendWatchMessage({ type: 'plans_update', plans: reachPlans, caregiverDisplayName: reachDisplayName, screen_settings: screenSettingsRef.current }).catch(() => {});
          // Also push PIN so the lock screen is in sync if it wasn't set during activation.
          const pinHash = userPinRef.current ?? null;
          const cogId = cognitoIdRef.current ?? null;
          if (typeof pinHash === 'string' && typeof cogId === 'string') {
            await sendWatchMessage({ type: 'pin_update', pin_hash: pinHash, cognito_id: cogId }).catch(() => {});
          }
        }
        break;
      }

      case 'request_plan_data': {
        const projectId = payload.projectId as string | undefined;
        const plans = await enrichPlansWithSOSTokens(buildPlans());
        const notes = projectId ? buildNotes(projectId) : [];
        const medications = projectId ? buildMedications(projectId) : [];
        watchInfo('watch-sync', 'Responding to request_plan_data', {
          projectId,
          planCount: plans.length,
          noteCount: notes.length,
          medCount: medications.length,
        });
        // Push PIN first so the lock screen is gated before content arrives.
        // Only send when both values are strings — WCSession rejects NSNull values.
        const pinHash = userPinRef.current ?? null;
        const cogId = cognitoIdRef.current ?? null;
        if (typeof pinHash === 'string' && typeof cogId === 'string') {
          await sendWatchMessage({ type: 'pin_update', pin_hash: pinHash, cognito_id: cogId }).catch(() => {});
        }
        // Push cached data immediately for instant display
        await sendWatchMessage({ type: 'plans_update', plans, caregiverDisplayName: getCaregiverDisplayName(), screen_settings: screenSettingsRef.current }).catch(() => {});
        if (projectId && notes.length > 0) {
          await sendWatchMessage({ type: 'notes_update', notes }).catch(() => {});
        }
        if (projectId && medications.length > 0) {
          await sendWatchMessage({ type: 'medications_update', medications }).catch(() => {});
        }
        // Trigger a background refresh — the notes/meds push effects will
        // automatically re-send when Redux state updates with fresh data.
        if (projectId) {
          dispatch(fetchNotes(projectId) as any).catch(() => {});
          dispatch(fetchMedications(projectId) as any).catch(() => {});
        }
        break;
      }

      case 'request_medications': {
        const projectId = payload.projectId as string | undefined;
        if (!projectId) break;

        // Fetch from API if not already cached in Redux
        const cached = medicationsRef.current[projectId];
        if (!cached || cached.length === 0) {
          await dispatch(fetchMedications(projectId) as any).catch(() => {});
        }

        const medications = buildMedications(projectId);
        watchInfo('watch-sync', 'Responding to request_medications', {
          projectId,
          count: medications.length,
        });
        await sendWatchMessage({ type: 'medications_update', medications }).catch(() => {});
        break;
      }

      case 'request_note_audio': {
        const noteId = payload.noteId as string | undefined;
        const aId = accountIdRef.current;
        if (!noteId || !aId) break;
        watchInfo('watch-sync', 'Sending note audio URL to Watch', { noteId });
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const { store } = require('../store') as { store: any };
          const token: string = store.getState()?.session?.token ?? '';
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const config = require('../config').default as { apiGateway: { endpoint: string } };
          const cId = store.getState()?.authentication?.user?.cognito_id ?? '';
          const audioUrl = `${config.apiGateway.endpoint}/notes/get-note-audio/${noteId}/${aId}/${cId}`;
          // Send URL + token so the Watch can download directly via URLSession.
          // This avoids WCSession.transferFile which is unreliable on simulator.
          await sendWatchMessage({
            type: 'note_audio_url',
            noteId,
            url: audioUrl,
            token,
          }).catch(() => {});
        } catch (err: any) {
          watchWarn('Failed to send note audio URL to Watch', { noteId, error: err?.message });
        }
        break;
      }

      case 'medication_taken':
      case 'medication_untaken': {
        const medId = payload.medId as string | undefined;
        const projectId = payload.projectId as string | undefined;
        const takenAt = payload.takenAt as string | undefined;
        const taken = eventType === 'medication_taken';
        if (!medId || !projectId) break;
        watchInfo('watch-sync', `Medication ${taken ? 'taken' : 'untaken'} from watch`, {
          medId,
          projectId,
          takenAt,
        });
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        if (!aId || !cId) break;
        try {
          const markedAt = takenAt ? Date.parse(takenAt) : Date.now();
          if (!Number.isFinite(markedAt)) throw new Error('Invalid dose timestamp');
          const result = await apiRecordsPost(
            `/medications/mark-taken/${medId}/${projectId}/${aId}/${cId}`,
            { taken, date: localDayKey(markedAt) },
          );
          if (!result?.success) throw new Error(result?.message || 'The dose mark was not saved');
        } catch (err: any) {
          watchWarn('Failed to log medication adherence from watch', { error: err?.message });
        }
        break;
      }

      case 'request_wellness': {
        const projectId = payload.projectId as string | undefined;
        if (!projectId) break;
        const wellness = await buildWellness(projectId);
        watchInfo('watch-sync', 'Responding to request_wellness', {
          projectId, state: wellness.state, available: wellness.available,
        });
        await sendWatchMessage({ type: 'wellness_update', wellness }).catch(() => {});
        break;
      }

      case 'wellness_im_ok': {
        // Recipient check-in from the wrist. This is the one watch action with
        // a live consequence on the phone side: it cancels an escalation the
        // care team may already be acting on, so a silent failure is not an
        // option — every path below reports back.
        const projectId = payload.projectId as string | undefined;
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        if (!projectId) break;
        if (!aId || !cId) {
          await sendWatchMessage({
            type: 'wellness_im_ok_result', projectId, ok: false, error: 'Sign in on iPhone',
          }).catch(() => {});
          break;
        }
        try {
          // Forward the watch's own stamp. transferUserInfo can defer delivery
          // for hours, and this event's whole meaning is WHEN it happened.
          const sentAt = typeof payload.sentAt === 'string' ? payload.sentAt : undefined;
          const res: any = await apiWellnessPost(
            `/im-ok/${projectId}/${aId}/${cId}`,
            sentAt ? { sent_at: sentAt } : {},
          );
          const ok = !!res?.success;
          watchInfo('watch-sync', 'Watch I\'m OK relayed', { projectId, ok });
          await sendWatchMessage({
            type: 'wellness_im_ok_result', projectId, ok,
            ...(ok ? {} : { error: res?.message || "Couldn't send" }),
          }).catch(() => {});
          // Push the refreshed rollup either way — on success it's the real
          // confirmation the watch swaps in for its optimistic echo.
          if (ok) {
            const wellness = await buildWellness(projectId);
            await sendWatchMessage({ type: 'wellness_update', wellness }).catch(() => {});
          }
        } catch (err: any) {
          watchWarn('Failed to relay watch I\'m OK', { projectId, error: err?.message });
          await sendWatchMessage({
            type: 'wellness_im_ok_result', projectId, ok: false, error: "Couldn't send",
          }).catch(() => {});
        }
        break;
      }

      case 'request_checkin_catalog': {
        // The watch renders its entry screens from this rather than a Swift
        // copy of the catalog. Units and server type names have to match the
        // endpoint exactly, and a hand-transcribed second table drifts.
        await sendWatchMessage({
          type: 'checkin_catalog', items: buildWatchCheckInCatalog(),
        }).catch(() => {});
        break;
      }

      case 'wellness_manual_entry': {
        // A reading taken on the wrist. Unlike I'm OK this has no escalation
        // consequence, but it does write to the health store the baselines are
        // computed from, so a wrong value is worse than a missing one — every
        // failure path reports back rather than leaving the watch to assume.
        const projectId = payload.projectId as string | undefined;
        const entries = Array.isArray(payload.entries) ? payload.entries : [];
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        if (!projectId) break;
        if (!aId || !cId) {
          await sendWatchMessage({
            type: 'wellness_manual_entry_result', projectId, ok: false, error: 'Sign in on iPhone',
          }).catch(() => {});
          break;
        }
        try {
          // `recordedAt` is the watch's own stamp and it is load-bearing.
          // WCSession falls back to transferUserInfo when the phone is out of
          // range, which can defer delivery for hours; a blood pressure taken
          // at 8am and filed as 2pm would land in the wrong daily aggregate
          // and quietly bend the personal baseline it is supposed to inform.
          // manual-entry accepts a per-entry `at`, clamped server-side to
          // [now - 7d, now + 5min], so forwarding it is both safe and correct.
          const recordedAt = typeof payload.recordedAt === 'string' ? payload.recordedAt : undefined;
          const built = entries
            .filter((e: any) => e && typeof e.type === 'string' && Number.isFinite(Number(e.value)))
            .map((e: any) => ({
              type: e.type as string,
              value: Number(e.value),
              ...(typeof e.unit === 'string' ? { unit: e.unit } : {}),
              ...(recordedAt ? { at: recordedAt } : {}),
            }));
          if (!built.length) {
            await sendWatchMessage({
              type: 'wellness_manual_entry_result', projectId, ok: false, error: 'Nothing to record',
            }).catch(() => {});
            break;
          }
          const res: any = await dispatch(recordWellnessCheckIn(projectId, { entries: built }) as any);
          const ok = !!res?.success;
          watchInfo('watch-sync', 'Watch manual entry relayed', {
            projectId, ok, count: built.length, types: built.map((e) => e.type),
          });
          await sendWatchMessage({
            type: 'wellness_manual_entry_result', projectId, ok,
            ...(ok ? {} : { error: res?.message || "Couldn't save" }),
          }).catch(() => {});
          if (ok) {
            const wellness = await buildWellness(projectId);
            await sendWatchMessage({ type: 'wellness_update', wellness }).catch(() => {});
          }
        } catch (err: any) {
          watchWarn('Failed to relay watch manual entry', { projectId, error: err?.message });
          await sendWatchMessage({
            type: 'wellness_manual_entry_result', projectId, ok: false, error: "Couldn't save",
          }).catch(() => {});
        }
        break;
      }

      case 'wellness_check_now': {
        // Caregiver counterpart — pushes the recipient's devices to check in.
        //
        // NOT fire-and-forget: apiPost resolves rather than throws on 4xx, and
        // this endpoint legitimately refuses (per-caller cooldown, missing
        // permission, no reachable device). Swallowing that told the caregiver
        // their nudge went out when it never did.
        const projectId = payload.projectId as string | undefined;
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        if (!projectId || !aId || !cId) break;
        let result: { ok: boolean; message: string };
        try {
          const res: any = await apiWellnessPost(`/check-now/${projectId}/${aId}/${cId}`, {});
          const pushed = res?.payload?.pushed_devices ?? 0;
          result = res?.success
            ? { ok: true, message: pushed > 0 ? 'Asked them to check in.' : 'No device to reach right now.' }
            : { ok: false, message: res?.message || 'Try again in a moment.' };
          watchInfo('watch-sync', 'Watch check-now relayed', { projectId, ok: result.ok, pushed });
        } catch (err: any) {
          watchWarn('Failed to relay watch check-now', { projectId, error: err?.message });
          result = { ok: false, message: "Couldn't send" };
        }
        await sendWatchMessage({ type: 'wellness_check_now_result', projectId, ...result }).catch(() => {});
        break;
      }

      case 'request_emergency_data': {
        const projectId = payload.projectId as string | undefined;
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        if (!projectId || !aId || !cId) break;

        // Always fetch fresh contacts so the watch doesn't get an empty list
        // when the mobile app hasn't navigated to the contacts screen yet.
        // Read the result from the store directly since the ref updates async.
        await dispatch(fetchContacts(projectId) as any).catch(() => {});
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { store } = require('../store') as { store: any };
        const freshContactList: any[] = store.getState()?.contacts?.list?.[projectId] ?? [];

        const contacts = buildEmergencyContacts(projectId, freshContactList);
        watchInfo('watch-sync', 'Responding to request_emergency_data', {
          projectId,
          contactCount: contacts.length,
        });

        // Generate a short-lived emergency card URL for the QR code (24h, 3 uses).
        let qrPayload: { url: string; expiresAt: string; matrix: number[]; size: number } | null = null;
        try {
          const res = await apiProjectsPost(`/generate-emergency-token/${projectId}/${aId}/${cId}`,
            { purpose: 'share' },
          );
          if (res?.payload?.url) {
            const { matrix, size } = buildQRMatrix(res.payload.url);
            qrPayload = {
              url: res.payload.url,
              expiresAt: res.payload.expires_at ?? new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
              matrix,
              size,
            };
          }
        } catch (err: any) {
          watchWarn('Emergency QR token generation failed', { error: err?.message });
        }

        await sendWatchMessage({
          type: 'emergency_update',
          projectId,
          contacts,
          qr: qrPayload,
        }).catch(() => {});
        break;
      }

      case 'trigger_sos': {
        const projectId = payload.projectId as string | undefined;
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        if (!projectId || !aId || !cId) break;

        // An SOS that sat in a queue (an older watch build fell back to
        // transferUserInfo while the phone was away) would alert contacts long
        // after the watch told the wearer to call 911, with a stale location.
        const triggeredAt = Date.parse(String(payload.triggeredAt ?? ''));
        if (Number.isFinite(triggeredAt) && Date.now() - triggeredAt > SOS_TRIGGER_MAX_AGE_MS) {
          watchWarn('Dropped a stale trigger_sos from the watch', { projectId, ageMs: Date.now() - triggeredAt });
          break;
        }

        const triggeredByRole = payload.triggeredByRole as string | undefined;
        const triggeredByName = payload.triggeredByName as string | undefined;

        watchInfo('watch-sync', 'Received trigger_sos from watch', { projectId, triggeredByRole });

        try {
          const res = await apiProjectsPost(`/emergency-sos/${projectId}/${aId}/${cId}`,
            {
              latitude:           payload.latitude,
              longitude:          payload.longitude,
              accuracy:           payload.accuracy,
              altitude:           payload.altitude,
              speed:              payload.speed,
              locationAge:        payload.locationAge,
              heartRate:          payload.heartRate,
              heartRateVariability: payload.heartRateVariability,
              bloodOxygen:        payload.bloodOxygen,
              activityState:      payload.activityState,
              impactDetected:     payload.impactDetected,
              peakGForce:         payload.peakGForce,
              batteryLevel:       payload.batteryLevel,
              triggeredByRole:    triggeredByRole ?? 'recipient',
              triggeredByName:    triggeredByName ?? undefined,
            },
          );
          await sendWatchMessage({
            type: 'sos_result',
            success: res?.success ?? false,
            contactsNotified: res?.payload?.contactsNotified ?? 0,
            cardUrl: res?.payload?.cardUrl ?? null,
            eventId: res?.payload?.eventId ?? null,
            message: res?.success ? null : 'Could not send alert',
          }).catch(() => {});
          watchInfo('watch-sync', 'SOS trigger complete', {
            projectId,
            contactsNotified: res?.payload?.contactsNotified,
          });
        } catch (err: any) {
          watchWarn('SOS trigger failed', { error: err?.message });
          await sendWatchMessage({
            type: 'sos_result',
            success: false,
            message: 'Server error — call 911 directly',
          }).catch(() => {});
        }
        break;
      }

      case 'request_sos_status': {
        // Watch is polling for the latest acknowledgement state on an
        // active SOS event. We hit /projects/emergency-sos-status which
        // returns the seen_by list (caregivers who opened the push) and
        // the original contactsNotified count, then ship it back as a
        // sos_status_update message that the EmergencySOSView consumes.
        // Failures are silent — the watch will retry on the next tick.
        const projectId = payload.projectId as string | undefined;
        const eventId = payload.eventId as string | undefined;
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        if (!projectId || !eventId || !aId || !cId) break;
        try {
          const res = await apiProjectsGet(`/emergency-sos-status/${projectId}/${aId}/${cId}/${encodeURIComponent(eventId)}`,
          );
          await sendWatchMessage({
            type: 'sos_status_update',
            projectId,
            eventId,
            seen_by: res?.payload?.seen_by ?? [],
            contactsNotified: res?.payload?.contactsNotified ?? 0,
          }).catch(() => {});
        } catch (err: any) {
          watchWarn('request_sos_status failed', { error: err?.message });
        }
        break;
      }

      case 'sos_token_spent': {
        // The watch sent SOS itself and the token is single-use (or the server
        // refused it): mint a fresh one and send it back with the plans.
        const projectId = payload.projectId as string | undefined;
        if (!projectId) break;
        sosTokensRef.current.delete(projectId);
        const plans = await enrichPlansWithSOSTokens(buildPlans());
        const message = { type: 'plans_update', plans, caregiverDisplayName: getCaregiverDisplayName(), screen_settings: screenSettingsRef.current };
        await pushWatchContext(message).catch(() => {});
        await sendWatchMessage(message).catch(() => {});
        watchInfo('watch-sync', 'Re-minted the watch SOS token after use', { projectId });
        break;
      }

      case 'file_received': {
        // A file was transferred from the watch. The metadata `type` field
        // distinguishes between a regular voice note and a caregiver SOS
        // reply — they get persisted as different note kinds.
        const innerType = (payload.type as string | undefined) || 'voice_note';
        if (innerType === 'sos_reply') {
          await handleSosReply({
            source: 'file',
            projectId: payload.projectId as string | undefined,
            localPath: payload.localPath as string | undefined,
            messageId: payload.messageId as string | undefined,
            senderName: payload.senderName as string | undefined,
            senderCognitoId: payload.senderCognitoId as string | undefined,
          });
          break;
        }

        const projectId = payload.projectId as string | undefined;
        const localPath = payload.localPath as string | undefined;
        const createdAt = payload.createdAt as string | undefined;
        // localId is the watch-side draft ID (e.g. "watch-<timestamp>"). We send it
        // back as note_synced so the watch can remove the local draft from its history.
        const localId = payload.localId as string | undefined;
        // transcript produced by on-device SFSpeechRecognizer on the watch (best-effort).
        // When present it becomes `body` so the backend summarization pipeline runs.
        const transcript = (payload.transcript as string | undefined) || undefined;
        const isEmergencyAudio = payload.emergency === true;

        if (!projectId || !localPath) {
          watchWarn('file_received event missing projectId or localPath', { projectId, localPath });
          break;
        }

        watchInfo('watch-sync', 'Uploading watch voice note to backend', {
          projectId,
          localPath,
          hasTranscript: !!transcript,
        });
        try {
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          const fs = require('expo-file-system/legacy') as {
            readAsStringAsync: (uri: string, opts: Record<string, string>) => Promise<string>;
            EncodingType?: { Base64?: string };
          };
          const base64 = await fs.readAsStringAsync(`file://${localPath}`, {
            encoding: fs.EncodingType?.Base64 ?? 'base64',
          });

          const fallbackTitle = isEmergencyAudio
            ? `Emergency Audio — ${new Date(createdAt ?? Date.now()).toLocaleString()}`
            : `Watch Note — ${new Date(createdAt ?? Date.now()).toLocaleString()}`;
          const title = transcript ? transcript.slice(0, 120) : fallbackTitle;
          // body is NOT NULL in the DB. Use the transcript when available so the
          // backend summarization model has text to work with; fall back to the title.
          const body = transcript || title;

          // get-note-audio expects a data URL (data:audio/m4a;base64,<data>).
          // expo-file-system returns plain base64 — add the prefix here.
          const rawDataUrl = `data:audio/m4a;base64,${base64}`;

          const result = await dispatch(
            createNote(projectId, {
              type: 'voice',
              raw: rawDataUrl,
              body,
              title,
              query: title,
              ...(isEmergencyAudio ? { note_type: 'emergency_audio' } : {}),
            }) as any,
          );
          watchInfo('watch-sync', 'Watch voice note upload complete', {
            projectId,
            success: result?.success ?? false,
            emergency: isEmergencyAudio,
          });
          if (result?.success !== false) {
            if (localId) {
              sendWatchMessage({ type: 'note_synced', localId }).catch(() => {});
            }
            dispatch(fetchNotes(projectId) as any).catch(() => {});
            if (!isEmergencyAudio) {
              notifyNoteProcessed(projectId, result?.payload, title, body);
            }

            // For emergency audio: send a follow-up SMS to emergency contacts with the
            // audio URL and transcription so they can listen directly from their phone.
            if (isEmergencyAudio) {
              const aId = accountIdRef.current;
              const cId = cognitoIdRef.current;
              const noteId = result?.payload?.id ?? result?.payload?.note_id;
              const audioUrl = noteId
                ? `${require('../config').default.apiGateway.endpoint}/notes/get-note-audio/${noteId}/${aId}/${cId}`
                : null;
              if (aId && cId && audioUrl) {
                apiProjectsPost(`/emergency-sos-audio/${projectId}/${aId}/${cId}`,
                  { noteId: String(noteId), audioUrl, transcription: transcript || null },
                ).catch(() => {});
              }
            }
          }
        } catch (err: any) {
          watchError('Watch voice note upload failed', { projectId, error: err?.message });
        }
        break;
      }

      case 'voice_note_inline': {
        // Watch sent audio inline via sendMessage (used on simulator where transferFile
        // is unreliable, and as the fast path on real device when the phone is reachable).
        const projectId = payload.projectId as string | undefined;
        const localId = payload.localId as string | undefined;
        const createdAt = payload.createdAt as string | undefined;
        const base64 = payload.audioBase64 as string | undefined;
        // SOS-triggered audio rides this same path with `emergency: true`
        // so the post-upload bookkeeping (note_type + follow-up SMS to
        // emergency contacts) runs the same way as the durable file path.
        const isEmergencyAudio = payload.emergency === true;

        if (!projectId || !base64) {
          watchWarn('voice_note_inline missing projectId or audioBase64', { projectId });
          break;
        }

        const transcript = payload.transcript as string | undefined;
        watchInfo('watch-sync', 'Uploading inline watch voice note', {
          projectId,
          hasTranscript: !!transcript,
          emergency: isEmergencyAudio,
        });
        try {
          const fallbackTitle = isEmergencyAudio
            ? `Emergency Audio — ${new Date(createdAt ?? Date.now()).toLocaleString()}`
            : `Watch Note — ${new Date(createdAt ?? Date.now()).toLocaleString()}`;
          const title = transcript ? transcript.slice(0, 120) : fallbackTitle;
          const body = transcript || title;
          // get-note-audio expects a data URL (data:audio/m4a;base64,<data>).
          // The watch sends plain base64 — add the prefix here.
          const rawDataUrl = `data:audio/m4a;base64,${base64}`;

          const result = await dispatch(
            createNote(projectId, {
              type: 'voice',
              raw: rawDataUrl,
              body,
              title,
              query: title,
              ...(isEmergencyAudio ? { note_type: 'emergency_audio' } : {}),
            }) as any,
          );
          watchInfo('watch-sync', 'Inline watch voice note upload complete', {
            projectId,
            success: result?.success ?? false,
            emergency: isEmergencyAudio,
          });
          if (result?.success !== false) {
            if (localId) {
              sendWatchMessage({ type: 'note_synced', localId }).catch(() => {});
            }
            dispatch(fetchNotes(projectId) as any).catch(() => {});
            if (!isEmergencyAudio) {
              notifyNoteProcessed(projectId, result?.payload, title, body);
            }

            if (isEmergencyAudio) {
              const aId = accountIdRef.current;
              const cId = cognitoIdRef.current;
              const noteId = result?.payload?.id ?? result?.payload?.note_id;
              const audioUrl = noteId
                ? `${require('../config').default.apiGateway.endpoint}/notes/get-note-audio/${noteId}/${aId}/${cId}`
                : null;
              if (aId && cId && audioUrl) {
                apiProjectsPost(`/emergency-sos-audio/${projectId}/${aId}/${cId}`,
                  { noteId: String(noteId), audioUrl, transcription: transcript || null },
                ).catch(() => {});
              }
            }
          }
        } catch (err: any) {
          watchError('Inline watch voice note upload failed', { projectId, error: err?.message });
        }
        break;
      }

      case 'sos_reply_inline': {
        // Recipient on the watch recorded a voice reply to a caregiver_message.
        // Same payload shape as voice_note_inline plus messageId/senderName so
        // we can dedupe against the durable file path and attribute the note.
        await handleSosReply({
          source: 'inline',
          projectId: payload.projectId as string | undefined,
          audioBase64: payload.audioBase64 as string | undefined,
          messageId: payload.messageId as string | undefined,
          senderName: payload.senderName as string | undefined,
          senderCognitoId: payload.senderCognitoId as string | undefined,
        });
        break;
      }

      case 'request_vault_items': {
        const projectId = payload.projectId as string | undefined;
        if (!projectId) break;
        const items = buildVaultItems(projectId);
        watchInfo('watch-sync', 'Responding to request_vault_items', { projectId, count: items.length });
        // Always send what we have immediately so the watch isn't waiting.
        await sendWatchMessage({ type: 'vault_items_update', projectId, items }).catch(() => {});
        // If the cache is empty the phone may not have loaded vault entries yet.
        // Fetch in the background; the vaultList effect (below) will push an
        // updated vault_items_update when the data arrives.
        if (items.length === 0) {
          const aId = accountIdRef.current;
          dispatch(fetchVaultEntries(projectId) as any).catch(() => {});
        }
        break;
      }

      case 'request_vault_qr': {
        // Watch user tapped a vault credential and wants a one-time QR shortlink.
        // Flow:
        //  1. Check in-memory VEK session cache — if warm, generate QR instantly.
        //  2. If not cached: tell the watch to show "Approve on iPhone", then ask
        //     the phone UI (via onVaultApproval callback) for explicit user approval.
        //  3. On approval: derive VEK from server salt (PBKDF2, no biometrics).
        //  4. Decrypt the credential, re-encrypt with a fresh OTK, create a
        //     short-lived share link, build the QR matrix, send to watch.
        const credentialId = payload.credentialId as number | undefined;
        const projectId = payload.projectId as string | undefined;
        const maxUses: number = (payload.maxUses as number | undefined) ?? 1;
        const expiresIn: number = (payload.expiresIn as number | undefined) ?? 300;
        const aId = accountIdRef.current;
        const cId = cognitoIdRef.current;
        if (!credentialId || !projectId || !aId || !cId) break;

        watchInfo('watch-sync', 'Processing request_vault_qr', { credentialId, projectId, expiresIn });

        try {
          // Find the encrypted credential in Redux.
          const projectCredentials = vaultListRef.current?.[projectId] ?? [];
          const encEntry = projectCredentials.find((c) => c.id === credentialId);
          if (!encEntry) {
            watchWarn('request_vault_qr: credential not found in Redux', { credentialId, projectId });
            await sendWatchMessage({ type: 'vault_qr_error', credentialId, error: 'not_found' }).catch(() => {});
            break;
          }

          const VEK_TTL_MS = 5 * 60 * 1000;
          const cached = watchVekCache.current.get(projectId);
          let vek: Uint8Array | null = (cached && cached.exp > Date.now()) ? cached.vek : null;

          if (!vek) {
            // VEK not in session cache. Ask the phone UI for explicit approval instead
            // of silently triggering biometrics — the user needs to be aware their
            // watch is requesting sensitive data, and Face ID from the background
            // is unreliable / confusing.
            await sendWatchMessage({
              type: 'vault_qr_status',
              credentialId,
              status: 'pending_approval',
            }).catch(() => {});

            if (onVaultApprovalRef.current) {
              vek = await new Promise<Uint8Array | null>((resolve) => {
                // 60-second hard timeout in case the user ignores the prompt.
                const timeout = setTimeout(() => resolve(null), 60_000);

                onVaultApprovalRef.current!({
                  credentialId,
                  projectId,
                  label: encEntry.label,
                  credentialType: encEntry.credential_type ?? 'login',
                  approve: async () => {
                    clearTimeout(timeout);
                    try {
                      // Phone is foregrounded and the user explicitly tapped
                      // Approve. openProjectVault resolves the project's key
                      // SCHEME first, then produces the key: a cached v2 key,
                      // an unwrap under the account root key, or — only for a
                      // project still on v1 — the PBKDF2 derive.
                      //
                      // Reading the cache directly (as this did) breaks the
                      // moment a project migrates: the v1 slot would answer for
                      // v2 ciphertext and the watch would render garbage. The
                      // scheme-namespaced cache still keeps the fast path fast,
                      // which matters against the watch's 30-second timeout.
                      const opened = await openProjectVault({
                        accountId: aId,
                        projectId,
                        cognitoId: cId,
                      });
                      if (opened.status === 'ready') {
                        watchVekCache.current.set(projectId, {
                          vek: opened.vek,
                          exp: Date.now() + VEK_TTL_MS,
                        });
                        resolve(opened.vek);
                        return true;
                      }
                      watchWarn('request_vault_qr: vault not openable', {
                        credentialId,
                        status: opened.status,
                      });
                    } catch (e) {
                      watchWarn('request_vault_qr: VEK load/derivation failed', {
                        credentialId,
                        error: (e as any)?.message,
                      });
                    }
                    resolve(null);
                    return false;
                  },
                  deny: () => {
                    clearTimeout(timeout);
                    resolve(null);
                  },
                });
              });
            }
          }

          if (!vek) {
            watchWarn('request_vault_qr: approval denied or VEK unavailable', { credentialId });
            await sendWatchMessage({ type: 'vault_qr_error', credentialId, error: 'auth_required' }).catch(() => {});
            break;
          }

          // Decrypt with the VEK.
          const plain = decryptCredential(vek, encEntry.encrypted_data, encEntry.iv, encEntry.auth_tag);
          if (!plain) {
            watchWarn('request_vault_qr: decryption failed', { credentialId });
            await sendWatchMessage({ type: 'vault_qr_error', credentialId, error: 'decrypt_failed' }).catch(() => {});
            break;
          }

          // Re-encrypt with a fresh OTK.
          const otk = await generateShareKey();
          const { encrypted_data, iv, auth_tag } = await encryptCredential(otk, plain);

          // Create the share shortlink on the backend.
          const shareRes = await apiPost(`/links/create/${aId}/${cId}`, {
            project_id: projectId,
            target_type: 'credential_share',
            target_id: `share:${projectId}`,
            credential_share_data: {
              encrypted_data,
              iv,
              auth_tag,
              label: encEntry.label,
              url: encEntry.url ?? undefined,
              username_hint: encEntry.username_hint ?? undefined,
            },
            expires_in: expiresIn,
            max_uses: maxUses,
          });

          if (!shareRes?.success || !shareRes?.payload?.short_url) {
            watchWarn('request_vault_qr: shortlink creation failed', { credentialId });
            await sendWatchMessage({ type: 'vault_qr_error', credentialId, error: 'share_failed' }).catch(() => {});
            break;
          }

          // Embed the OTK in the URL fragment (never sent to the server).
          const fullUrl = `${shareRes.payload.short_url}#${bytesToHex(otk)}`;
          const expiresAt = shareRes.payload.expires_at
            ? new Date(shareRes.payload.expires_at * 1000).toISOString()
            : new Date(Date.now() + expiresIn * 1000).toISOString();

          const { matrix, size } = buildQRMatrix(fullUrl);
          watchInfo('watch-sync', 'Sending vault QR to watch', { credentialId, expiresAt });

          await sendWatchMessage({
            type: 'vault_qr',
            credentialId,
            projectId,
            url: fullUrl,
            expiresAt,
            matrix,
            size,
            maxUses,
          }).catch(() => {});
        } catch (err: any) {
          watchError('request_vault_qr failed', { credentialId, error: err?.message });
          await sendWatchMessage({ type: 'vault_qr_error', credentialId, error: 'internal' }).catch(() => {});
        }
        break;
      }

      case 'request_vault_totp': {
        // Watch user tapped the 2FA button for a login credential.
        // Flow mirrors request_vault_qr but skips share-link creation:
        //  1. VEK warm → decrypt → generate code → send instantly.
        //  2. VEK cold → ask for phone-side approval → decrypt → send code.
        const credentialId = payload.credentialId as number | undefined;
        const projectId = payload.projectId as string | undefined;
        if (!credentialId || !projectId) break;

        watchInfo('watch-sync', 'Processing request_vault_totp', { credentialId, projectId });

        try {
          const projectCredentials = vaultListRef.current?.[projectId] ?? [];
          const encEntry = projectCredentials.find((c) => c.id === credentialId);
          if (!encEntry) {
            await sendWatchMessage({ type: 'vault_totp_error', credentialId, error: 'not_found' }).catch(() => {});
            break;
          }

          const VEK_TTL_MS = 5 * 60 * 1000;
          const cached = watchVekCache.current.get(projectId);
          let vek: Uint8Array | null = (cached && cached.exp > Date.now()) ? cached.vek : null;

          if (!vek) {
            await sendWatchMessage({ type: 'vault_totp_status', credentialId, status: 'pending_approval' }).catch(() => {});

            if (onVaultApprovalRef.current) {
              vek = await new Promise<Uint8Array | null>((resolve) => {
                const timeout = setTimeout(() => resolve(null), 60_000);
                onVaultApprovalRef.current!({
                  credentialId,
                  projectId,
                  label: encEntry.label,
                  credentialType: encEntry.credential_type ?? 'login',
                  approve: async () => {
                    clearTimeout(timeout);
                    try {
                      // Same reasoning as the QR path above: resolve the
                      // scheme before the key, or a migrated project hands the
                      // watch a v1 key for v2 ciphertext.
                      const aId = accountIdRef.current;
                      const cId = cognitoIdRef.current;
                      if (aId && cId) {
                        const opened = await openProjectVault({
                          accountId: aId,
                          projectId,
                          cognitoId: cId,
                        });
                        if (opened.status === 'ready') {
                          watchVekCache.current.set(projectId, { vek: opened.vek, exp: Date.now() + VEK_TTL_MS });
                          resolve(opened.vek);
                          return true;
                        }
                        watchWarn('request_vault_totp: vault not openable', {
                          credentialId,
                          status: opened.status,
                        });
                      }
                    } catch (e) {
                      watchWarn('request_vault_totp: VEK load failed', { credentialId, error: (e as any)?.message });
                    }
                    resolve(null);
                    return false;
                  },
                  deny: () => { clearTimeout(timeout); resolve(null); },
                });
              });
            }
          }

          if (!vek) {
            await sendWatchMessage({ type: 'vault_totp_error', credentialId, error: 'auth_required' }).catch(() => {});
            break;
          }

          const plain = decryptCredential(vek, encEntry.encrypted_data, encEntry.iv, encEntry.auth_tag);
          if (!plain?.otp_secret) {
            // No TOTP secret on this credential.
            await sendWatchMessage({ type: 'vault_totp_response', credentialId, projectId, code: null, secondsLeft: 0, hasTOTP: false }).catch(() => {});
            break;
          }

          // Generate current code + time remaining.
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const { TOTP } = require('otpauth') as typeof import('otpauth');
          const secret = plain.otp_secret.trim().toUpperCase().replace(/\s/g, '');
          const totp = new TOTP({ secret, digits: 6, period: 30 });
          const code = totp.generate();
          const secondsLeft = 30 - (Math.floor(Date.now() / 1000) % 30);

          watchInfo('watch-sync', 'Sending TOTP code to watch', { credentialId, secondsLeft });
          await sendWatchMessage({ type: 'vault_totp_response', credentialId, projectId, code, secondsLeft, hasTOTP: true }).catch(() => {});

          // Also push updated vault items with hasTOTP now populated (VEK is warm).
          const updatedItems = buildVaultItems(projectId);
          await sendWatchMessage({ type: 'vault_items_update', projectId, items: updatedItems }).catch(() => {});
        } catch (err: any) {
          watchError('request_vault_totp failed', { credentialId, error: err?.message });
          await sendWatchMessage({ type: 'vault_totp_error', credentialId, error: 'internal' }).catch(() => {});
        }
        break;
      }

      default:
        // Queue sync status events (queued / syncing / synced / failed) are emitted by the
        // same channel; nothing to do on the phone side for those.
        break;
    }
  }, [buildPlans, buildNotes, buildMedications, buildWellness, buildEmergencyContacts, buildVaultItems, dispatch, handleSosReply, notifyNoteProcessed]);

  // Stable ref so the notification effect always calls the latest handleEvent
  // without adding it as a dependency (avoids re-triggering on every render).
  const handleEventRef = useRef(handleEvent);
  useEffect(() => { handleEventRef.current = handleEvent; }, [handleEvent]);

  // Process any vault approval that arrived via local notification while the
  // app was backgrounded. Set by setPendingWatchVaultApproval (called from
  // RootNavigator's notification response handler). Runs once enabled + authed.
  useEffect(() => {
    if (!enabled || !_pendingWatchVaultApproval) return;
    const pending = _pendingWatchVaultApproval;
    _pendingWatchVaultApproval = null;
    let cancelled = false;

    const run = async () => {
      // Ensure vault entries are loaded (may need re-fetch on cold start).
      const projectCredentials = vaultListRef.current?.[pending.projectId];
      if (!projectCredentials || projectCredentials.length === 0) {
        await dispatch(fetchVaultEntries(pending.projectId) as any).catch(() => {});
        await new Promise<void>((resolve) => setTimeout(resolve, 300));
      }
      if (cancelled) return;
      // Trigger the full request_vault_qr flow — will invoke onVaultApproval
      // with approve/deny callbacks, showing the approval sheet on the phone.
      await handleEventRef.current({
        correlationId: `notification-${pending.credentialId}`,
        status: 'synced',
        type: 'request_vault_qr',
        payload: {
          type: 'request_vault_qr',
          credentialId: pending.credentialId,
          projectId: pending.projectId,
        },
      } as any).catch(() => {});
    };

    const timer = setTimeout(() => run().catch(() => {}), 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, dispatch]);

  // Subscribe to watch events for the duration of the hook's enabled life.
  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = onWatchSyncStatus(handleEvent);
    return unsubscribe;
  }, [enabled, handleEvent]);

  /** Display name of the logged-in user — sent with every plans_update so the
   *  watch can identify caregivers in SOS SMS messages. */
  const getCaregiverDisplayName = useCallback((): string | null => {
    const u = currentUserRef.current;
    if (!u) return null;
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ');
    return full || u.email || null;
  }, []);

  // Proactively push plans whenever the project list changes.
  // Always bundle PIN into the same context push so the two never clobber each other
  // (updateApplicationContext is a single-slot store — the last write wins).
  // SOS tokens are fetched async and included before sending so the watch
  // always gets a fresh 48h token without a separate round-trip.
  useEffect(() => {
    if (!enabled || Object.keys(projectsList).length === 0) return;
    let cancelled = false;
    (async () => {
      const plans = await enrichPlansWithSOSTokens(buildPlans());
      if (cancelled) return;
      const caregiverDisplayName = getCaregiverDisplayName();
      pushWatchContext({
        type: 'plans_update',
        plans,
        pin_hash: userPinRef.current ?? null,
        cognito_id: cognitoIdRef.current ?? null,
        caregiverDisplayName,
        screen_settings: screenSettingsRef.current,
      }).catch(() => {});
      sendWatchMessage({
        type: 'plans_update',
        plans,
        caregiverDisplayName,
        screen_settings: screenSettingsRef.current,
      }).catch(() => {});
    })();
    return () => { cancelled = true; };
  }, [enabled, projectsList, accountSosCapable, screenSettings, buildPlans, enrichPlansWithSOSTokens]);

  // Push PIN config to Watch whenever the user's PIN is set, changed, or removed.
  // Only send as a live message (no pushWatchContext here — the plans effect already
  // bundles PIN into the context push to prevent the two from clobbering each other).
  // Guard against null values: WCSession rejects NSNull in sendMessage dictionaries.
  useEffect(() => {
    if (!enabled) return;
    const pinHash = userPin ?? null;
    const cogId = cognitoId ?? null;
    if (typeof pinHash === 'string' && typeof cogId === 'string') {
      sendWatchMessage({ type: 'pin_update', pin_hash: pinHash, cognito_id: cogId }).catch(() => {});
    }
  }, [enabled, userPin, cognitoId]);

  // Push fresh notes to Watch whenever the Redux notes cache updates.
  // This fires automatically after the background fetchNotes dispatch in request_plan_data
  // and after a watch voice note is uploaded (file_received triggers fetchNotes on success).
  useEffect(() => {
    if (!enabled) return;
    Object.keys(notesList).forEach(projectId => {
      const notes = buildNotes(projectId);
      if (notes.length > 0) {
        sendWatchMessage({ type: 'notes_update', notes }).catch(() => {});
      }
    });
  }, [enabled, notesList, buildNotes]);

  // Push fresh medications to Watch whenever the Redux medications cache updates.
  // This fires automatically after the background fetchMedications dispatch in
  // request_plan_data and request_medications, keeping the watch list in sync.
  useEffect(() => {
    if (!enabled) return;
    Object.keys(medicationsList).forEach(projectId => {
      const medications = buildMedications(projectId);
      if (medications.length > 0) {
        sendWatchMessage({ type: 'medications_update', medications }).catch(() => {});
      }
    });
  }, [enabled, medicationsList, buildMedications]);

  // Push emergency contacts to Watch whenever the Redux contacts cache updates.
  // Omitting the "qr" key ensures this does not clear an existing QR code on the
  // watch. The QR is only sent in the full request_emergency_data response flow.
  useEffect(() => {
    if (!enabled) return;
    Object.keys(contactsList).forEach(projectId => {
      const emergencyContacts = buildEmergencyContacts(projectId);
      sendWatchMessage({ type: 'emergency_update', projectId, contacts: emergencyContacts }).catch(() => {});
    });
  }, [enabled, contactsList, buildEmergencyContacts]);

  // Push watch-shared vault items to the watch whenever the vault Redux cache
  // changes (e.g. after fetchVaultEntries or a toggleVaultWatchShared dispatch).
  // Only sends metadata — no encrypted data is included.
  useEffect(() => {
    if (!enabled || !vaultList) return;
    Object.keys(vaultList).forEach(projectId => {
      const items = buildVaultItems(projectId);
      sendWatchMessage({ type: 'vault_items_update', projectId, items }).catch(() => {});
    });
  }, [enabled, vaultList, buildVaultItems]);

  // Push fresh plans whenever the phone app comes to the foreground.
  // This is the primary recovery path for the "no plans" scenario:
  //   1. Watch app opens while phone is backgrounded → empty state (phone can't send).
  //   2. User picks up phone — AppState changes to "active".
  //   3. Phone immediately pushes plans → watch shows content within ~1 s.
  useEffect(() => {
    if (!enabled) return;
    let lastState = AppState.currentState;
    const sub = AppState.addEventListener('change', (nextState) => {
      const wasBackground = lastState === 'background' || lastState === 'inactive';
      lastState = nextState;
      if (wasBackground && nextState === 'active') {
        (async () => {
          const plans = await enrichPlansWithSOSTokens(buildPlans());
          const caregiverDisplayName = getCaregiverDisplayName();
          watchInfo('watch-sync', 'App foregrounded — pushing plans to watch', { count: plans.length });
          // Both paths: context for background delivery, sendMessage for real-time.
          pushWatchContext({
            type: 'plans_update',
            plans,
            pin_hash: userPinRef.current ?? null,
            cognito_id: cognitoIdRef.current ?? null,
            caregiverDisplayName,
            screen_settings: screenSettingsRef.current,
          }).catch(() => {});
          sendWatchMessage({ type: 'plans_update', plans, caregiverDisplayName, screen_settings: screenSettingsRef.current }).catch(() => {});
        })().catch(() => {});
      }
    });
    return () => sub.remove();
  }, [enabled, buildPlans, enrichPlansWithSOSTokens, getCaregiverDisplayName]);
}
